import { type InputHTMLAttributes, type ReactNode, type Ref, useId } from "react";
import { cn } from "../../lib/cn";
import { controlClass, describedBy, Field } from "./field";

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  /** Fixed text before the value, e.g. "+91". */
  prefix?: string;
  /** Control after the value, e.g. a show-password button. */
  trailing?: ReactNode;
  /** Decorative icon inside the field, before the value (e.g. a search glass). */
  leadingIcon?: ReactNode;
  id?: string;
  fieldClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({
  label,
  hint,
  error,
  optional,
  prefix,
  trailing,
  leadingIcon,
  id,
  className,
  fieldClassName,
  ref,
  ...inputProps
}: TextFieldProps) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <Field
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      optional={optional}
      className={fieldClassName}
    >
      <div className="relative flex items-stretch">
        {prefix && (
          <span className="flex items-center rounded-l-md border border-edge-strong border-r-0 bg-surface-2 px-3 text-ink-2 text-sm">
            {prefix}
          </span>
        )}
        {leadingIcon && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-3"
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(inputId, hint, error)}
          className={cn(
            controlClass,
            prefix && "rounded-l-none",
            trailing && "pr-12",
            leadingIcon && "pl-10",
            className,
          )}
          {...inputProps}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-1">{trailing}</div>
        )}
      </div>
    </Field>
  );
}
